import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { Stop as StopIcon } from "@mui/icons-material";
import io from "socket.io-client";
import { toast } from "react-toastify";
import { MYM_GAME_LABELS } from "./mym/useMymTitlesConfig";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const getAdminToken = () =>
  localStorage.getItem("admin_token") || localStorage.getItem("auth_token");

const ForceStopHeaderButton = () => {
  const [forcedId, setForcedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const socketRef = useRef(null);

  const fetchForced = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`);
      if (!res.ok) return;
      const data = await res.json();
      const forced =
        Array.isArray(data.config) && data.config.find((item) => item.forceModal);
      setForcedId(forced?.id || null);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchForced();
  }, [fetchForced]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;
    socket.on("mym.force_open", (payload) => {
      setForcedId(payload?.id || null);
      if (!payload?.id) fetchForced();
    });
    socket.on("mym.force_clear", () => {
      setForcedId(null);
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [fetchForced]);

  const handleForceStop = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/mym-force-stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAdminToken()}`,
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to stop force");
      }
      setForcedId(null);
      toast.success("Force mode stopped");
    } catch (err) {
      toast.error(err.message || "Failed to stop force");
    } finally {
      setBusy(false);
    }
  };

  const label = forcedId ? MYM_GAME_LABELS[forcedId] || forcedId : null;

  return (
    <Tooltip
      title={
        label
          ? `Stop force for ${label}`
          : "No game is currently forced"
      }
    >
      <span>
        <Button
          size="small"
          variant="contained"
          startIcon={
            busy ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <StopIcon sx={{ fontSize: 16 }} />
            )
          }
          disabled={!forcedId || busy}
          onClick={handleForceStop}
          sx={{
            bgcolor: forcedId ? "#d32f2f" : "rgba(0,0,0,0.25)",
            color: "#fff",
            fontWeight: 700,
            textTransform: "none",
            fontSize: "0.75rem",
            height: 32,
            px: 1.5,
            whiteSpace: "nowrap",
            "&:hover": {
              bgcolor: forcedId ? "#b71c1c" : "rgba(0,0,0,0.25)",
            },
            "&.Mui-disabled": {
              bgcolor: "rgba(0,0,0,0.2)",
              color: "rgba(255,255,255,0.55)",
            },
          }}
        >
          Force Stop
        </Button>
      </span>
    </Tooltip>
  );
};

export default ForceStopHeaderButton;
